import {
  BriefcaseIcon,
  TableCellsIcon,
  CurrencyDollarIcon,
  UserIcon,
  WrenchScrewdriverIcon,
  UserPlusIcon,
  AdjustmentsHorizontalIcon,
  ChatBubbleBottomCenterTextIcon,
  StarIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { useLocation } from "react-router-dom";
import {
  NavigationSidebar,
  NavSection,
} from "@/core/components/navigation-sidebar";
import { isProductEnabled } from "@/business/product-access";
import { useGetBusinessReviewAccess } from "@/business/query";
import { useBusiness } from "@/core/business";
import { useBusinessProducts } from "@/core/business/use-business-products";
import { useCanViewPayrollRoster } from "@/payroll/roster/permissions";
import {
  PAYROLL_SETTINGS_ROSTER_PATH,
  PAYROLL_SETTINGS_PATH,
} from "@/payroll/settings/routes";
import { useGetMyUser } from "@/user/query";
import { isAdminOrOwnerOrManager } from "@/user/utils";
import {
  BUSINESS_SETTINGS_ROUTES,
  SIDEBAR_MODES,
  SIDEBAR_SECTIONS,
} from "./constants";
import { useBusinessSettingsSidebar } from "./context/business-settings-sidebar-context";

interface BusinessSettingsNavigationProps {
  businessId?: string;
  userRole?: string;
  isLoading?: boolean;
}

interface BuildSectionsParams {
  showPayments: boolean;
  isSpiffOnly: boolean;
  hasReviewAccess: boolean;
  enableReferrals: boolean;
}

const buildNavigationSections = ({
  showPayments,
  isSpiffOnly,
  hasReviewAccess,
  enableReferrals,
}: BuildSectionsParams): NavSection[] => [
  {
    items: [
      {
        title: "Business",
        href: BUSINESS_SETTINGS_ROUTES.business,
        icon: BriefcaseIcon,
        show: true,
      },
      {
        title: "Payments",
        href: BUSINESS_SETTINGS_ROUTES.payments,
        icon: CurrencyDollarIcon,
        show: enableReferrals && showPayments && !isSpiffOnly,
      },
      {
        title: "Users",
        href: BUSINESS_SETTINGS_ROUTES.users,
        icon: UserIcon,
        show: true,
      },
      {
        title: "Customers",
        href: BUSINESS_SETTINGS_ROUTES.customers,
        icon: UserPlusIcon,
        show: enableReferrals && !isSpiffOnly,
      },
      {
        title: "Referral History",
        href: BUSINESS_SETTINGS_ROUTES.referralHistory,
        icon: WrenchScrewdriverIcon,
        show: enableReferrals && !isSpiffOnly,
      },
      {
        title: "Referral Sources",
        href: BUSINESS_SETTINGS_ROUTES.referralSources,
        icon: AdjustmentsHorizontalIcon,
        show: enableReferrals && !isSpiffOnly,
      },
      {
        title: "Text Messages",
        href: BUSINESS_SETTINGS_ROUTES.textMessages,
        icon: ChatBubbleBottomCenterTextIcon,
        show: enableReferrals && !isSpiffOnly,
      },
      {
        title: "Five-star reviews",
        href: BUSINESS_SETTINGS_ROUTES.reviews,
        icon: StarIcon,
        show: enableReferrals && !isSpiffOnly && hasReviewAccess,
      },
      {
        title: "Legacy Approvals",
        href: BUSINESS_SETTINGS_ROUTES.legacyApprovals,
        icon: DocumentTextIcon,
        show: enableReferrals,
      },
    ],
  },
];

const BusinessSettingsNavigation = ({
  businessId,
  userRole,
  isLoading,
}: BusinessSettingsNavigationProps) => {
  const { isSpiffOnly, selectedBusiness } = useBusiness();
  const { data: user } = useGetMyUser();
  const { settings } = useBusinessProducts();
  const enableReferrals = isProductEnabled(settings, "referrals");
  const { canViewRoster } = useCanViewPayrollRoster(selectedBusiness, user);
  const location = useLocation();
  const { updateSidebar } = useBusinessSettingsSidebar();
  const { data: reviewAccessData } = useGetBusinessReviewAccess(
    enableReferrals ? businessId : undefined,
  );

  const showPayments = isAdminOrOwnerOrManager(userRole);
  const hasReviewAccess = reviewAccessData?.has_review_access ?? false;

  const businessPath = `/business-settings/${businessId}`;
  const items = buildNavigationSections({
    showPayments,
    isSpiffOnly,
    hasReviewAccess,
    enableReferrals,
  })[0].items.map((item) => ({
    ...item,
    href: `${businessPath}${item.href}`,
    exact: item.href === BUSINESS_SETTINGS_ROUTES.business,
  }));
  const sections: NavSection[] = [
    {
      title: canViewRoster ? "Referrals" : undefined,
      items: enableReferrals ? items.filter((item) => item.show !== false) : [],
    },
    {
      title: enableReferrals ? "Payroll" : undefined,
      items: canViewRoster
        ? [
            {
              title: "Roster",
              href: PAYROLL_SETTINGS_ROSTER_PATH,
              icon: TableCellsIcon,
            },
          ]
        : [],
    },
  ].filter((section) => section.items.length > 0);

  const handleNavigate = (_href: string) => {
    updateSidebar({
      isOpen: false,
      mode: SIDEBAR_MODES.view,
      section: SIDEBAR_SECTIONS.users,
      selectedItemId: undefined,
    });
  };

  return (
    <NavigationSidebar
      basePath=""
      defaultHref={
        location.pathname === PAYROLL_SETTINGS_PATH
          ? PAYROLL_SETTINGS_ROSTER_PATH
          : undefined
      }
      sections={sections}
      isLoading={isLoading}
      onNavigate={handleNavigate}
    />
  );
};

export default BusinessSettingsNavigation;
